import React from 'react';

import { Segmented } from 'antd';

import styled from 'styled-components';

import { isFolderOfType } from '@libs/NwFolders';

import { useFolders } from '@hooks/API/UseFolders';

import FolderSelect from '@components/ContactsFolders/FolderSelect';
import {
    useContactsSelector,
    useContactsSelectorDispatch,
} from '@components/ContactsSelector/Common/ContactsSelectorContext';
import { FilterRow, FilterLabel, FilterWrapper } from '@components/Filters/FilterRowUI';

const Wrapper = styled.div``;

const Hint = styled.p`
    font-size: ${(props) => props.theme.FontSizeSm};
    margin: 0 0 1rem 0;
`;

//il controllo cresce in altezza quando compare la dropdown: la label resta in cima
const FolderFilterRow = styled(FilterRow)`
    align-items: flex-start;

    ${FilterLabel} {
        padding-top: 0.5rem;
    }

    @media (max-width: ${(props) => props.theme.NarrowScreenMaxWidth}) {
        align-items: stretch;
    }
`;

const SelectRow = styled.div`
    margin-top: 0.5rem;
`;

const SelectHint = styled.small`
    display: block;
    margin-top: 0.25rem;
    font-size: ${(props) => props.theme.FontSizeSm};
    color: ${(props) => props.theme.TextColorMuted};
`;

/**
 * WithFolders e WithoutFolders sono due filtri indipendenti e combinabili
 * ("in queste folder ma non in quelle"): un array di ID vale "in quelle folder" /
 * "non in quelle folder", un array vuoto vale "in almeno una folder" / "in nessuna folder",
 * il filtro assente toglie il vincolo. Il pannello dedicato serve a gestire l'array vuoto,
 * che il filtro generico scarterebbe.
 */
const INCLUSION_ALL = 'all';
const INCLUSION_IN = 'in';
const EXCLUSION_ALL = 'all';
const EXCLUSION_NOT_IN = 'not-in';

const ModelFilterDrawerFoldersSection = () => {
    const { selection } = useContactsSelector();
    const cs_dispatch = useContactsSelectorDispatch();
    const { filters } = selection;

    const { data: folders } = useFolders();
    const hasModelFolders = folders ? folders.some((folder) => isFolderOfType(folder, 'model')) : false;

    //il valore arriva anche da smart list salvate: si considera solo se è davvero un array
    const withFilter = filters.working.find((f) => f.name === 'WithFolders' && Array.isArray(f.value));
    const withoutFilter = filters.working.find((f) => f.name === 'WithoutFolders' && Array.isArray(f.value));

    const withIds = withFilter ? withFilter.value : [];
    const withoutIds = withoutFilter ? withoutFilter.value : [];

    const inclusion = withFilter ? INCLUSION_IN : INCLUSION_ALL;
    const exclusion = withoutFilter ? EXCLUSION_NOT_IN : EXCLUSION_ALL;

    //i due filtri si riscrivono sempre insieme: null significa filtro disattivato
    const applyFolderFilters = (nextWith, nextWithout) => {
        const newWorkingFilters = filters.working.filter(
            (f) => f.name !== 'WithFolders' && f.name !== 'WithoutFolders'
        );
        if (nextWith) {
            newWorkingFilters.push({ name: 'WithFolders', value: nextWith, source: 'filter' });
        }
        if (nextWithout) {
            newWorkingFilters.push({ name: 'WithoutFolders', value: nextWithout, source: 'filter' });
        }
        cs_dispatch({ type: 'ChangeFilters', filters: newWorkingFilters });
    };

    const currentWith = withFilter ? withIds : null;
    const currentWithout = withoutFilter ? withoutIds : null;

    const handleChangeInclusion = (value) => {
        applyFolderFilters(value === INCLUSION_IN ? withIds : null, currentWithout);
    };

    const handleChangeExclusion = (value) => {
        applyFolderFilters(currentWith, value === EXCLUSION_NOT_IN ? withoutIds : null);
    };

    const handleChangeWithFolders = (ids) => {
        applyFolderFilters(ids, currentWithout);
    };

    const handleChangeWithoutFolders = (ids) => {
        applyFolderFilters(currentWith, ids);
    };

    if (!hasModelFolders) {
        return (
            <Wrapper>
                <Hint>no model folders available</Hint>
            </Wrapper>
        );
    }

    return (
        <Wrapper>
            <FolderFilterRow>
                <FilterLabel className={withFilter ? 'active' : ''}>Models in folders:</FilterLabel>
                <FilterWrapper>
                    <Segmented
                        value={inclusion}
                        options={[
                            { label: 'All', value: INCLUSION_ALL },
                            { label: 'In some folder', value: INCLUSION_IN },
                        ]}
                        onChange={handleChangeInclusion}
                    />
                    {withFilter && (
                        <SelectRow>
                            <FolderSelect
                                multiple
                                allowClear
                                placeholder="any folder"
                                value={withIds}
                                onChange={handleChangeWithFolders}
                                excludedIds={withoutIds}
                                getPopupContainer={(triggerNode) => triggerNode.parentNode}
                            />
                            <SelectHint>
                                {withIds.length > 0
                                    ? 'models in at least one of the selected folders'
                                    : 'models that belong to at least one folder'}
                            </SelectHint>
                        </SelectRow>
                    )}
                </FilterWrapper>
            </FolderFilterRow>
            <FolderFilterRow>
                <FilterLabel className={withoutFilter ? 'active' : ''}>Models not in folders:</FilterLabel>
                <FilterWrapper>
                    <Segmented
                        value={exclusion}
                        options={[
                            { label: 'All', value: EXCLUSION_ALL },
                            { label: 'Not in folder', value: EXCLUSION_NOT_IN },
                        ]}
                        onChange={handleChangeExclusion}
                    />
                    {withoutFilter && (
                        <SelectRow>
                            <FolderSelect
                                multiple
                                allowClear
                                placeholder="any folder"
                                value={withoutIds}
                                onChange={handleChangeWithoutFolders}
                                excludedIds={withIds}
                                getPopupContainer={(triggerNode) => triggerNode.parentNode}
                            />
                            <SelectHint>
                                {withoutIds.length > 0
                                    ? 'models not in any of the selected folders'
                                    : "models that don't belong to any folder"}
                            </SelectHint>
                        </SelectRow>
                    )}
                </FilterWrapper>
            </FolderFilterRow>
        </Wrapper>
    );
};

export default ModelFilterDrawerFoldersSection;
